import React from 'react';
import type { StudioWatermarkLogoId } from '../constants';

export const STUDIO_WATERMARK_LOGOS: Array<{
  id: StudioWatermarkLogoId;
  labelId: string;
}> = [
  { id: 'coin', labelId: 'hot-topic-studio-watermark-logo-coin' },
  { id: 'user', labelId: 'hot-topic-studio-watermark-logo-user' },
  { id: 'compass', labelId: 'hot-topic-studio-watermark-logo-compass' },
  { id: 'trophy', labelId: 'hot-topic-studio-watermark-logo-trophy' },
  { id: 'star', labelId: 'hot-topic-studio-watermark-logo-star' },
  { id: 'leaf', labelId: 'hot-topic-studio-watermark-logo-leaf' },
  { id: 'gift', labelId: 'hot-topic-studio-watermark-logo-gift' },
  { id: 'chart', labelId: 'hot-topic-studio-watermark-logo-chart' },
  { id: 'idea', labelId: 'hot-topic-studio-watermark-logo-idea' },
  { id: 'shield', labelId: 'hot-topic-studio-watermark-logo-shield' }
];

interface LogoWatermarkIconProps {
  logo: StudioWatermarkLogoId;
  size?: number | string;
  className?: string;
  title?: string;
}

const LogoWatermarkIcon: React.FC<LogoWatermarkIconProps> = ({ logo, size = 24, className, title }) => {
  const artwork = {
    coin: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M15.2 8.7c-.7-.8-1.7-1.2-3-1.2-1.7 0-2.8.8-2.8 2 0 3.3 5.4 1.4 5.4 4.7 0 1.3-1.1 2.2-2.9 2.2-1.3 0-2.4-.5-3.1-1.3M12 5.8v12.4" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.3 19c.7-3.4 3.1-5.3 6.7-5.3s6 1.9 6.7 5.3" />
      </>
    ),
    compass: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="m14.9 9.1-2.1 4-4 2.1 2.1-4z" />
        <path d="M12 5.2v1.4M12 17.4v1.4M5.2 12h1.4M17.4 12h1.4" />
      </>
    ),
    trophy: (
      <>
        <path d="M8 4.5h8v4.2c0 3-1.6 5.1-4 5.1s-4-2.1-4-5.1zM12 13.8v3.7M8.5 19.5h7" />
        <path d="M8 6H5v1.8c0 2.2 1.3 3.7 3.6 3.9M16 6h3v1.8c0 2.2-1.3 3.7-3.6 3.9" />
      </>
    ),
    star: <path d="m12 3.5 2.55 5.17 5.7.83-4.13 4.02.98 5.68L12 16.52 6.9 19.2l.98-5.68L3.75 9.5l5.7-.83z" />,
    leaf: (
      <>
        <path d="M19.8 4.2C12.2 4.5 6.5 7.7 5.2 13c-.8 3.2 1.2 5.9 4.5 5.6 5.4-.5 8.5-6.3 10.1-14.4Z" />
        <path d="M5.1 19.7c2.3-4.4 5.7-7.5 10.5-9.8" />
      </>
    ),
    gift: (
      <>
        <path d="M4 10h16v10H4zM3.5 7h17v3h-17zM12 7v13" />
        <path d="M12 7H8.7C6.6 7 6 4.2 7.9 3.5 9.8 2.8 11.1 5 12 7ZM12 7h3.3c2.1 0 2.7-2.8.8-3.5C14.2 2.8 12.9 5 12 7Z" />
      </>
    ),
    chart: (
      <>
        <path d="M4 19.5V5M4 19.5h16" />
        <path d="m7 15 3.2-3.2 2.7 2.2 5-6" />
        <path d="M14.7 8h3.2v3.2" />
      </>
    ),
    idea: (
      <>
        <path d="M8.4 15.3a6 6 0 1 1 7.2 0c-.9.7-1.3 1.4-1.4 2.2H9.8c-.1-.8-.5-1.5-1.4-2.2Z" />
        <path d="M9.8 20h4.4M12 3V1.8M5.6 5.6 4.7 4.7M18.4 5.6l.9-.9M4 12H2.8M21.2 12H20" />
      </>
    ),
    shield: (
      <>
        <path d="M12 3.2c2.1 1.7 4.3 2.5 6.6 2.6v5.4c0 4.1-2.4 7.4-6.6 9.6-4.2-2.2-6.6-5.5-6.6-9.6V5.8c2.3-.1 4.5-.9 6.6-2.6Z" />
        <path d="m9 12.1 2 2 4.2-4.3" />
      </>
    )
  }[logo];

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {artwork}
    </svg>
  );
};

interface CanvasLogoWatermarkProps {
  logo?: StudioWatermarkLogoId | null;
  color?: string;
  isStory?: boolean;
}

export const CanvasLogoWatermark: React.FC<CanvasLogoWatermarkProps> = ({
  logo,
  color = '#000000',
  isStory = false
}) => {
  if (!logo) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        right: isStory ? 24 : 20,
        bottom: isStory ? 26 : 22,
        zIndex: 0,
        width: isStory ? 104 : 88,
        height: isStory ? 104 : 88,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        opacity: 0.07,
        pointerEvents: 'none',
        userSelect: 'none'
      }}
    >
      <LogoWatermarkIcon logo={logo} size="100%" />
    </div>
  );
};

export default LogoWatermarkIcon;
